import { useState } from 'react';
import { Bell, Settings } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useUnreadNotificationCount } from '@/hooks/useUnreadNotificationCount';
import { STRINGS } from '@/strings/strings';
import { useAuthStore } from '@/stores/authStore';
import { WORLDS, type WorldSlug } from '@/lib/worlds';

const WORLDS_IN_SUBNAV_ORDER = [WORLDS.AA, WORLDS.SB, WORLDS.KP] as const;

interface TopBarProps {
  className?: string;
  /**
   * Override for the unread-notifications count. When omitted, the
   * `useUnreadNotificationCount()` polling hook supplies the value
   * (Batch 9C Commit 2 wiring; replaces the prior `?? 0` stub).
   * Override stays available for tests + special cases.
   */
  unreadCountOverride?: number;
  /**
   * Override for the active world. When omitted, derived from URL via
   * `useLocation()` — paths of shape `/builder/{aa|sb|kp}` match their slug.
   */
  activeWorldOverride?: WorldSlug | null;
}

export function TopBar({ className, unreadCountOverride, activeWorldOverride }: TopBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { count: hookUnreadCount } = useUnreadNotificationCount();
  const user = useAuthStore((s) => s.user);
  // Task 3 Commit B (Sebastian ruling 2026-09-09) — gear branches on
  // the both-worlds signal from /me (landed in Task 3 Commit A). A
  // customer WITH both worlds gets a two-entry menu; anyone else
  // (anonymous or single-world) keeps today's direct-navigate gear.
  // Popover state is controlled internally so menu-item clicks can
  // close before firing their navigate calls — mirrors the audio-side
  // precedent at `NowPlayingMenu.tsx:40,46`.
  const [gearMenuOpen, setGearMenuOpen] = useState(false);
  const showBothWorldsMenu =
    user !== null && user.hasBookWorld && user.hasAudioWorld;

  const unreadCount = unreadCountOverride ?? hookUnreadCount;
  const activeWorld =
    activeWorldOverride !== undefined
      ? activeWorldOverride
      : deriveActiveWorldFromPath(location.pathname);

  return (
    <header
      className={cn('sticky top-0 z-40 bg-dream-navy', className)}
      style={{ paddingTop: 'env(safe-area-inset-top, 0)' }}
    >
      <ContentContainer>
        <div className="flex items-center justify-between border-b border-stroke-on-dark py-3">
          <img src="/logo-dreamable.svg" alt="Dreamable" className="h-[18.656px] w-20 shrink-0" />
          <div className="flex items-center gap-2">
            <TopBarIconButton
              icon={Bell}
              hasUnread={unreadCount > 0}
              onClick={() => navigate('/account/notifications')}
              ariaLabel="Benachrichtigungen"
            />
            {showBothWorldsMenu ? (
              <Popover open={gearMenuOpen} onOpenChange={setGearMenuOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label="Einstellungen"
                    className="relative text-dream-white transition-colors hover:text-dream-white/80"
                  >
                    <Settings className="w-4 h-4" strokeWidth={2} />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" sideOffset={8} className="w-56 p-1">
                  <TopBarMenuItem
                    label={STRINGS.shell.topBar.menuProfil}
                    onClick={() => {
                      setGearMenuOpen(false);
                      navigate('/account');
                    }}
                  />
                  <TopBarMenuItem
                    label={STRINGS.shell.topBar.menuToBooks}
                    onClick={() => {
                      setGearMenuOpen(false);
                      // Sebastian ruling 2026-09-15 (round 2) — a
                      // customer who deliberately reaches for this
                      // entry wants the overview (Meine Bücher), not
                      // the most-recently-touched order. The
                      // auto-redirect on login into `pendingBookOrderId`
                      // (Case 1 of `computePostAuthRedirect`) stays
                      // untouched — that's a resume, not a browse.
                      navigate('/book/meine');
                    }}
                  />
                </PopoverContent>
              </Popover>
            ) : (
              <TopBarIconButton
                icon={Settings}
                onClick={() => navigate('/account')}
                ariaLabel="Einstellungen"
              />
            )}
          </div>
        </div>
      </ContentContainer>
      <ContentContainer>
        <nav
          aria-label="Welten"
          className="flex items-center justify-center gap-4 pt-2.5 pb-2 md:gap-7 desktop:gap-7 desktop:pt-4 desktop:pb-3.5"
        >
          {WORLDS_IN_SUBNAV_ORDER.map((world) => (
            <TopBarSubnavTab
              key={world.slug}
              name={world.name}
              active={world.slug === activeWorld}
              onClick={() => navigate(`/builder/${world.slug}`)}
            />
          ))}
        </nav>
      </ContentContainer>
    </header>
  );
}

function deriveActiveWorldFromPath(pathname: string): WorldSlug | null {
  const match = /^\/builder\/(aa|sb|kp)(?:\/|$)/.exec(pathname);
  if (!match || !match[1]) return null;
  return match[1] as WorldSlug;
}

interface TopBarIconButtonProps {
  icon: typeof Bell;
  hasUnread?: boolean;
  onClick: () => void;
  ariaLabel: string;
}

function TopBarIconButton({
  icon: Icon,
  hasUnread = false,
  onClick,
  ariaLabel,
}: TopBarIconButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="relative text-dream-white transition-colors hover:text-dream-white/80"
    >
      <Icon className="w-4 h-4" strokeWidth={2} />
      {hasUnread && (
        <span
          data-testid="topbar-unread-dot"
          aria-hidden="true"
          className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-notification-red"
        />
      )}
    </button>
  );
}

const subnavTabVariants = cva(
  'relative flex flex-col items-center justify-center gap-0.5 font-medium whitespace-nowrap leading-none transition-colors text-xs desktop:text-sm',
  {
    variants: {
      active: {
        true: 'text-world-aa',
        false: 'text-dream-white',
      },
    },
    defaultVariants: { active: false },
  },
);

interface TopBarSubnavTabProps extends VariantProps<typeof subnavTabVariants> {
  name: string;
  onClick: () => void;
}

function TopBarSubnavTab({ name, active, onClick }: TopBarSubnavTabProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={subnavTabVariants({ active })}
    >
      <span>{name}</span>
      {active && <span aria-hidden="true" className="h-px w-full bg-world-aa" />}
    </button>
  );
}

interface TopBarMenuItemProps {
  label: string;
  onClick: () => void;
}

function TopBarMenuItem({ label, onClick }: TopBarMenuItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-muted"
    >
      <span>{label}</span>
    </button>
  );
}
